import { useRef, useState, useEffect } from 'react';
import { Camera, RotateCcw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface BackgroundImageUploaderProps {
  currentImage: string;
  onImageUpload: (file: File) => Promise<boolean>;
  onResetToDefault: () => void;
  isLoading: boolean;
  allowImageChange?: boolean;
  className?: string;
}

export function BackgroundImageUploader({
  currentImage,
  onImageUpload,
  onResetToDefault,
  isLoading,
  allowImageChange = true,
  className = ''
}: BackgroundImageUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showControls, setShowControls] = useState(false);
  
  // Masquer les contrôles si clic ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.bg-uploader-controls') && !target.closest('.bg-uploader-trigger')) {
        setShowControls(false);
      }
    };

    if (showControls) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showControls]);

  const handleImageClick = () => {
    if (allowImageChange && !isLoading) {
      setShowControls(true);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const success = await onImageUpload(file);
    if (success) {
      setShowControls(false);
    }
    
    // Reset input pour permettre le même fichier
    event.target.value = '';
  };

  const triggerFileSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleReset = (e: React.MouseEvent) => {
    e.stopPropagation();
    onResetToDefault();
    setShowControls(false);
  };

  console.log('🖼️ BackgroundImageUploader render:', { currentImage, isLoading, allowImageChange });

  return (
    <div className={`relative min-h-full w-full ${className}`}>
      {/* Input file caché */}
      {allowImageChange && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      )}

      {/* Zone cliquable pour l'image de fond */}
      <div 
        className={`bg-uploader-trigger absolute inset-0 bg-cover bg-center bg-no-repeat group ${
          allowImageChange && !isLoading ? 'cursor-pointer' : ''
        }`}
        style={{ backgroundImage: `url(${currentImage})` }}
        onClick={handleImageClick}
      >
        {/* Gradient Overlay - restauré comme avant pour le contraste */}
        <div 
          className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/75 transition-opacity duration-300" 
          style={{ zIndex: 1 }}
        />
        
        {/* Indicateur de chargement */}
        {isLoading && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-30">
            <div className="bg-white/10 backdrop-blur-sm rounded-full p-4">
              <Loader2 className="h-8 w-8 text-white animate-spin" />
            </div>
          </div>
        )}

        {/* Contrôles au clic uniquement - icône seule */}
        {allowImageChange && showControls && !isLoading && (
          <div className="bg-uploader-controls absolute bottom-20 left-1/2 transform -translate-x-1/2 z-50">
            <Button
              onClick={triggerFileSelect}
              size="sm"
              className="bg-black/40 backdrop-blur-md border border-white/30 hover:bg-black/60 text-white shadow-2xl rounded-full p-4"
              aria-label="Changer l'image de fond"
            >
              <Camera className="h-6 w-6" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}